import * as THREE from 'three/webgpu';

/**
 * @file Runtime texture loader — reads `public/textures/manifest.json`
 * (generated by `npm run textures` / `tools/fetch-textures.mjs`) and turns
 * requested slugs into ready-to-use `THREE.Texture`s configured per the
 * plan's "Textures" decision: tiling ground/facade maps get mipmaps +
 * `LinearFilter` + anisotropy (NearestFilter shimmers at the game's ~52°
 * camera pitch). NearestFilter is reserved for sprites/window masks/decals,
 * which do NOT go through this loader.
 *
 * `three/webgpu` ONLY — never bare `'three'` (project rule).
 */

/** @typedef {import('../types.js').EngineCaps} EngineCaps */

/**
 * One entry in `manifest.json`'s `assets` map.
 * @typedef {Object} TextureManifestAsset
 * @property {string} file Absolute-from-site-root URL, e.g.
 *   `'/textures/city/asphalt.jpg'`.
 * @property {number} size Longest-side px the file was generated at
 *   (informational — the file itself is the source of truth).
 * @property {string} srcId ambientCG asset id the file was generated from.
 * @property {string} set `'city' | 'forest' | 'wasteland' | ...`.
 * @property {number} [meanLinear] Mean LINEAR-space RGB of the file, measured
 *   by `tools/fetch-textures.mjs` with sharp. Input to {@link texBoost}.
 *   Absent on manifests written before that field existed.
 */

/**
 * `public/textures/manifest.json`'s shape.
 * @typedef {Object} TextureManifest
 * @property {string} generated ISO timestamp of the fetch run that wrote it.
 * @property {string} license `'CC0'`.
 * @property {Object<string, TextureManifestAsset>} assets Keyed by slug.
 */

/** Caches in-flight/completed manifest fetches by URL so repeated
 *  `loadTextureSet` calls (different level loads, gallery, dev scenes)
 *  don't refetch the same JSON. */
const manifestCache = new Map();

/**
 * Fetches and parses the texture manifest.
 * @param {string} [url='/textures/manifest.json']
 * @returns {Promise<TextureManifest>}
 */
export async function loadManifest(url = '/textures/manifest.json') {
  const cached = manifestCache.get(url);
  if (cached) return cached;

  const promise = (async () => {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`loadManifest: HTTP ${res.status} for "${url}"`);
    return /** @type {Promise<TextureManifest>} */ (res.json());
  })();

  manifestCache.set(url, promise);
  try {
    return await promise;
  } catch (err) {
    manifestCache.delete(url); // don't poison the cache with a failed fetch
    throw err;
  }
}

/**
 * ALBEDO NORMALISATION — the reason a textured level is not black.
 *
 * `bakedMaterial` multiplies the baked vertex colour by the colour map, and
 * the bake already multiplied the palette albedo by the light. The map's own
 * mean lands on top as a THIRD multiply, and these maps are DARK in linear
 * space: `asphalt` averages 0.056, `road_lines` 0.032. Albedo 0.10 x light
 * 1.37 x map 0.056 = 0.008 — black tarmac at noon.
 *
 * The fix is to scale the map back up so it contributes VARIATION and not
 * BRIGHTNESS. `texBoost` computes that scale from the `meanLinear` the fetch
 * tool measured, so a new level gets it for free instead of hand-measuring a
 * table:
 *
 * ```js
 * const manifest = await loadManifest();
 * const boost = texBoost(manifest, 'asphalt');           // -> 8 (capped)
 * bakedMaterial({ map, uvScale, tint: [boost, boost, boost] });
 * ```
 *
 * `target` is the mean the normalised map should land on — deliberately BELOW
 * 1.0, because a fully normalised ground map puts a sunlit surface over the
 * day bloom threshold and the whole level starts glowing (this is exactly the
 * correction the three hand-tuned tables apply by eye). `max` caps the
 * near-black maps, where `target/meanLinear` runs away and one bright JPEG
 * speck would punch through the bloom threshold.
 *
 * The three existing levels keep their hand-measured tables
 * (`levels/city/index.js#TEX_BOOST`, `levels/forest/config.js#texBoost`,
 * `levels/wasteland/config.js#WASTE_TEX_BOOST`) — those were verified
 * visually in the browser and this function does not reproduce them exactly.
 * New levels should use this.
 *
 * @param {TextureManifest|null|undefined} manifest From {@link loadManifest}.
 * @param {string} slug
 * @param {{target?: number, max?: number}} [opts]
 * @returns {number} `min(max, target / meanLinear)`. Returns `1` (a no-op
 *   multiply) when the slug is unknown or the manifest predates `meanLinear`
 *   — dark, but never a NaN tint.
 */
export function texBoost(manifest, slug, { target = 0.55, max = 8 } = {}) {
  const mean = manifest?.assets?.[slug]?.meanLinear;
  if (typeof mean !== 'number' || !(mean > 0)) return 1;
  return Math.min(max, target / mean);
}

const loader = new THREE.TextureLoader();

/**
 * Loads a set of manifest slugs as ready-to-use tiling textures.
 *
 * Configuration applied to every texture (per the plan's tiling-texture
 * defaults): `colorSpace = SRGBColorSpace`, `wrapS/wrapT = RepeatWrapping`,
 * `generateMipmaps = true`, `minFilter = LinearMipmapLinearFilter`,
 * `magFilter = LinearFilter`, `anisotropy = min(4, caps.maxAnisotropy)`.
 *
 * Unknown slugs (not present in the manifest — e.g. `npm run textures`
 * hasn't been run, or a typo) are logged and simply omitted from the
 * returned Map rather than throwing, so a level with one bad slug can still
 * render everything else.
 *
 * @param {string[]} names Slugs to load, e.g. `['asphalt', 'road_lines']`.
 * @param {{caps?: EngineCaps, manifestUrl?: string}} [opts]
 * @returns {Promise<Map<string, import('three/webgpu').Texture>>} Keyed by slug.
 */
export async function loadTextureSet(names, opts = {}) {
  const { caps, manifestUrl = '/textures/manifest.json' } = opts;
  const manifest = await loadManifest(manifestUrl);
  const anisotropy = Math.min(4, caps?.maxAnisotropy ?? 4);

  /** @type {Map<string, import('three/webgpu').Texture>} */
  const out = new Map();

  await Promise.all(
    (names ?? []).map(async (slug) => {
      const entry = manifest.assets?.[slug];
      if (!entry) {
        console.warn(`[assets/textures] no manifest entry for slug "${slug}" (run \`npm run textures\`?)`);
        return;
      }

      let tex;
      try {
        tex = await loader.loadAsync(entry.file);
      } catch (err) {
        console.warn(`[assets/textures] failed to load "${slug}" (${entry.file}):`, err);
        return;
      }

      tex.name = slug;
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.generateMipmaps = true;
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.anisotropy = anisotropy;

      out.set(slug, tex);
    }),
  );

  return out;
}
